import { FlatTreeControl } from '@angular/cdk/tree';

import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeModule,
} from '@angular/material/tree';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

import { TreeItemFlatNode } from '../models/tree/tree-item-flat-node.model';
import { TreeItemNode } from '../models/tree/tree-item-node.model';
import { TreeNestedNode } from '../models/tree/tree-nested-node.model';

@Component({
    selector: 'cerrix-tree',
    imports: [
    MatTreeModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    MatCheckboxModule,
    MatDividerModule,
    FormsModule,
    MatInputModule,
    MatTooltipModule
],
    providers: [],
    templateUrl: './cerrix-tree.component.html',
    styleUrl: './cerrix-tree.component.scss'
})
export class CerrixTreeComponent implements OnInit {
  @Input() public selection: string | string[] | undefined;
  @Input() public treeData: TreeItemNode[] = [];
  @Input() public multiple: boolean = false;
  @Input() public dataUiid: string = "undefined"

  @Input() public includeChildren: boolean = false;
  @Output() public includeChildrenChange: EventEmitter<boolean> =
    new EventEmitter();

  private readonly destroyRef = inject(DestroyRef);

  protected filterValue: string = '';
  private readonly searchFilter$: Subject<string> = new Subject<string>();

  private readonly _transformer = (node: TreeNestedNode, level: number) => {
    node.item.hasChildren = !!node.children && node.children.length > 0;
    node.item.level = level;

    return node.item;
  };

  protected treeControl = new FlatTreeControl<TreeItemFlatNode>(
    node => node.level,
    node => node.hasChildren
  );

  private readonly treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.hasChildren,
    node => node.children
  );

  protected dataSource = new MatTreeFlatDataSource(
    this.treeControl,
    this.treeFlattener
  );

  ngOnInit() {
    this.dataSource.data = this.getDataSource(this.treeData);
    this.expandSelected();

    this.searchFilter$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        if (value) {
          this.filterTree(value.toLowerCase());
        } else {
          this.resetTreeFilter();
        }
      });
  }

  public getSelectedNodes() {
    let selectedList: TreeItemNode[] = [];

    this.treeControl.dataNodes.forEach(node => {
      if (node.selected) {
        selectedList.push(<TreeItemNode>node);
      }
    });

    return selectedList;
  }

  protected hasChild = (_: number, nodeData: TreeItemFlatNode) =>
    nodeData.hasChildren;

  protected filterChanged(event?: Event): void {
    let filterValue = '';
    if (event) {
      filterValue = (event.target as HTMLInputElement).value;
    }

    this.filterValue = filterValue;
    this.searchFilter$.next(filterValue);
  }

  protected expandAll() {
    this.treeControl.expandAll();
  }

  protected collapseAll() {
    this.treeControl.collapseAll();
  }

  protected expandSelected(): void {
    this.treeControl.dataNodes.forEach(node => {
      if (node.selected || node.childSelected) {
        this.treeControl.expand(node);
      }
    });
  }

  protected selectAll(select: boolean) {
    this.treeControl.dataNodes.forEach(node => {
      if (!node.hidden) {
        node.selected = select;
      }
    });

    this.setIndeterminate();
  }

  protected toggleIncludeChildren() {
    this.includeChildrenChange.emit(!this.includeChildren);
  }

  protected singleSelectChange(node: TreeItemFlatNode) {
    const previousSelected = this.treeControl.dataNodes.find(n => n.selected);
    if (previousSelected) {
      previousSelected.selected = false;
    }

    node.selected = true;

    this.setIndeterminate();
  }

  protected multiSelectChange(node: TreeItemFlatNode) {
    if (this.includeChildren) {
      node.selected = !node.selected;
      this.treeControl.getDescendants(node).forEach(child => {
        child.selected = node.selected;
      });
    } else {
      node.selected = !node.selected;
    }

    this.setIndeterminate();
  }

  private filterTree(value: string) {
    this.dataSource.data.forEach(node => {
      this.filterNode(node, value);
    });
  }

  private filterNode(node: TreeNestedNode, value: string): boolean {
    const hasMatch = node.item.name.toLowerCase().includes(value);
    let anyChildrenMatch = false;

    if (node.item.hasChildren) {
      node.children.forEach(child => {
        const childrenMatch = this.filterNode(child, value);
        anyChildrenMatch ||= childrenMatch;
      });
    }

    node.item.hidden = !hasMatch && !anyChildrenMatch;
    node.item.highlighted = hasMatch;

    if (!node.item.parentId && !node.item.hidden) {
      this.treeControl.expandDescendants(node.item);
    }

    return hasMatch || anyChildrenMatch;
  }

  private resetTreeFilter() {
    this.dataSource.data.forEach(node => {
      node.item.hidden = false;
      node.item.highlighted = false;
      this.treeControl.getDescendants(node.item).forEach(child => {
        child.hidden = false;
        child.highlighted = false;
      });
    });

    this.treeControl.collapseAll();
  }

  private setIndeterminate() {
    const func = (nodes: TreeNestedNode[]): boolean => {
      let anySelected = false;
      nodes.forEach(node => {
        const childSelected = node.children && func(node.children);
        node.item.childSelected = childSelected && !node.item.selected;
        if (node.item.selected || childSelected) {
          anySelected = true;
        }
      });

      return anySelected;
    };

    func(this.dataSource.data);
  }

  private getDataSource(treeData: TreeItemNode[]): TreeNestedNode[] {
    const nestedTree = this.mapArrayToTree(treeData);
    this.setSelection(nestedTree);

    return nestedTree;
  }

  private setSelection(nestedTree: TreeNestedNode[]): boolean {
    const values = this.selection
      ? this.multiple
        ? <string[]>this.selection
        : [<string>this.selection]
      : [];

    let anySelected = false;

    nestedTree.forEach(node => {
      node.item.selected = values.includes(node.item.id);
      node.item.childSelected =
        node.children && this.setSelection(node.children);

      anySelected =
        anySelected || node.item.selected || node.item.childSelected;
    });

    nestedTree.sort((n1, n2) => n1.item.order - n2.item.order);

    return anySelected;
  }

  private mapArrayToTree(dataset: TreeItemNode[]): TreeNestedNode[] {
    const nestedTree: TreeNestedNode[] = [];

    if (dataset) {
      const hashTable = Object.create(null);
      dataset.forEach(
        item => (hashTable[item.id] = { item: { ...item }, children: [] })
      );

      dataset.forEach(item => {
        if (item.parentId && hashTable[item.parentId])
          hashTable[item.parentId].children.push(hashTable[item.id]);
        else nestedTree.push(hashTable[item.id]);
      });
    }

    return nestedTree;
  }
}
